/**
 * Reload the mailbox after the MAILBOX ITSELF has changed — switching to another
 * of the owner's connected Google accounts, stepping into or out of a rep's
 * mailbox, or being ejected from an impersonation the server refused.
 *
 * All of those are a hard document reload by design: the mailbox's state is
 * spread across a root-scoped service, three sibling panes and an in-memory
 * thread cache, and re-fetching every bit of it as the new account is what the
 * reload buys. It also means there is nothing to cancel — no request from the old
 * account outlives the document, so a stale response cannot land in the new
 * account's UI.
 *
 * One thing does outlive it, and it is the bug this file exists for: the URL.
 * `?thread=` is how the mailbox holds the open conversation (see the
 * `selectedThread` writer in email.component.ts), and `location.reload()` replays
 * the WHOLE current URL. So switching accounts while an email was open
 * re-requested THAT email from the NEW mailbox:
 *
 *     Account A → open email A → URL is /email?thread=<A's thread id>
 *     switch to Account B → reload() → /email?thread=<A's thread id>
 *     GET /gmail/threads/<A's thread id>   ← asked of Account B
 *
 * A Gmail thread id only means anything inside the account it came from, so
 * Account B answers 404 — and the console had no way out of it, because nothing
 * cleaned the parameter up: every later refresh of that tab asked again.
 *
 * So the mailbox ROUTE is reloaded, not the current URL. Every query parameter
 * `/email` takes is either account-scoped (`thread`) or a one-shot from the OAuth
 * callback (`connected`, `as`, `mail`, `error`, `reason`) — re-arming one of those
 * on a switch would restage the "Connecting…" card for a round trip that is not
 * happening. None of them may outlive the account it was written for.
 */

/**
 * Load `/email` fresh, dropping the query string the current document carries.
 *
 * `location.replace()` rather than the `location.assign()` / `location.href =`
 * this console uses everywhere else it navigates the document (export-pdf,
 * preview-detail, the OAuth hand-off in email-sidebar). That is deliberate and
 * not a slip to tidy up: those calls all go somewhere NEW, where a history entry
 * is what the user wants. Here the entry being left names a conversation in the
 * account being left, so keeping it means browser Back returns to
 * `/email?thread=<other account's id>` and asks for it again — the same 404, one
 * keypress away. `replace` retires that entry with the account it belonged to.
 *
 * Angular's `Location.replaceState()` is not an option either: it rewrites the
 * URL without reloading, and the reload is the entire point of a switch.
 */
export function reloadIntoMailbox(): void {
  if (window.location.search || window.location.hash) {
    window.location.replace(window.location.pathname);
    return;
  }
  // Nothing account-scoped to shed — say "load this again" with the method that
  // means exactly that, rather than assigning an identical href.
  window.location.reload();
}
